import { useCallback, useEffect } from 'react';
import { AGENDA_REFRESH_RATE } from '@utils/constants';
import {
    useLogicalDaysAtom,
    usePreviewAgendaDiscounts,
} from '@states/logicalDay';
import { useResourcesAtom, useSelectedResourcesIds } from '@states/resources';
import useLogicalDay from '@utils/hooks/agenda/useLogicalDay';
import useResources from '@utils/hooks/agenda/useResources';
import logs from '@utils/logs/firebase';
import { useAgendaSyncAtom } from '@states/agenda';

export type UseSynchro = (
    autoSynchro?: boolean,
) => {
    synchronize: () => void;
    hardSynchronize: () => void;
    softReset: () => void;
    hardReset: () => void;
    isSynchronizing?: boolean;
};

// We need to have this oustide of the react context to avoid infinite re-render loop
let autoSynchroInterval: NodeJS.Timeout;

const useSynchro: UseSynchro = (autoSynchro = false) => {
    const { isLoading: isLogicalDayLoading } = useLogicalDay();
    const { fetchResources, isLoading: isResourceLoading } = useResources();
    const { reset: resetLogicalDay } = useLogicalDaysAtom();
    const { reset: resetResources } = useResourcesAtom();
    const { reset: resetSelectedResourcesIds } = useSelectedResourcesIds();
    const {
        value: isSynchronizing,
        set: setIsSynchronizing,
    } = useAgendaSyncAtom();

    const { reset: resetPreviewDiscounts } = usePreviewAgendaDiscounts();

    const fetchData = useCallback(async () => {
        void (await fetchResources());
    }, [fetchResources]);

    const softReset = useCallback(() => {
        resetLogicalDay();
        resetResources();
    }, [resetLogicalDay, resetResources]);

    // hardreset also reset 'preferences' such as selected resources etc...
    const hardReset = useCallback(() => {
        softReset();
        resetSelectedResourcesIds();
    }, [resetSelectedResourcesIds, softReset]);

    // Hard synchronize that will reset all concerned atoms before fetching data.
    const hardSynchronize = useCallback(async () => {
        if (isSynchronizing) {
            return;
        }

        softReset();
        logs.agendaRefresh();
        await fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData, softReset]);

    // Soft synchronize that will not fire if any data is already loading
    const synchronize = useCallback(async () => {
        if (isLogicalDayLoading || isResourceLoading) {
            return;
        }
        setIsSynchronizing(true);
        await fetchData();
        resetPreviewDiscounts();
        setIsSynchronizing(false);
    }, [
        fetchData,
        isLogicalDayLoading,
        isResourceLoading,
        setIsSynchronizing,
        resetPreviewDiscounts,
    ]);

    // Autosynchro
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (autoSynchro) {
            if (autoSynchroInterval) {
                clearInterval(autoSynchroInterval);
            }
            autoSynchroInterval = setInterval(() => {
                void synchronize();
            }, AGENDA_REFRESH_RATE);
            return () => clearInterval(autoSynchroInterval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [synchronize]);

    return {
        synchronize,
        hardSynchronize,
        softReset,
        hardReset,
        isSynchronizing,
    };
};

export default useSynchro;
